import { z } from 'zod'
import { BundleModelFactory, BundleDataType } from './bundle.db'
import type {
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from 'firebase/firestore'
import { ModelUtils, vector3 } from './utils'
import type { QueryDocumentSnapshot as AdminQueryDocumentSnapshot } from 'firebase-admin/firestore'
import type { Timestamp as AdminTimestamp } from 'firebase-admin/firestore'

export const BundleSharedDetailsDBModel = z.object({
  title: z.string().optional(),
  email: z.string().optional(),
  description: z.string().optional(),
  location_name: z.string().optional(),
  wall_mounted: z.boolean().optional(),
})

export class BundleSharedModelFactory {
  public BundleSharedObjectScanDBModel
  public BundleSharedMetadataDBModel
  public BundleSharedDBModel
  public FirestoreBundleSharedDBModel
  public FirestoreBundleSharedMetadata
  public sharedBundleFromBundle
  public fromOwnerEditable
  public sharedBundleConverter
  public adminSharedBundleConverter
  private sharedObjectScanFromBundleObjectScan
  private sharedMetadataFromBundleMetadata
  private utils

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(timestamp: typeof Timestamp | typeof AdminTimestamp) {
    this.utils = new ModelUtils(timestamp)
    const {
      BundleMonocleVideoDBModel,
      BundleDBModel,
      BundleMetadataDBModel,
      BundleObjectScanDBModel,
      BundleOwnerEditableDBModel,
    } = new BundleModelFactory(timestamp)

    this.BundleSharedObjectScanDBModel = z.object({
      /**
       * Min bound of vertices in scan (meters)
       * [x, y, z](readonly)
       */
      min_coordinate: vector3,
      /**
       * Max bound of vertices in scan (meters)
       * [x, y, z](readonly)
       */
      max_coordinate: vector3,
      /**
       * Camera euler rotation angles (degrees)
       * Pitch (the x component) is the rotation about the x-axis.
       * Yaw (the y component) is the rotation about the y-axis.
       * Roll (the z component) is the rotation about the z-axis.
       * (default: [0, 0, 0])
       */
      camera_euler_angles: vector3.default([0, 0, 0]),
      /**
       * Camera pan (meters)
       * [x, y, z]
       * (default: [0, 0, 0])
       */
      camera_pan: vector3.optional(),
      /**
       * Accessible storage path when the GLB file is finished uploading
       */
      glb_filepath: z.string().optional(),
      /**
       * Accessible storage path when the USDZ file is finished uploading
       */
      usdz_filepath: z.string().optional(),
    })

    this.BundleSharedMetadataDBModel = z.object({
      /**
       * (readonly)
       * Owner of the scan
       */
      owner: z.string(),
      /**
       * (readonly)
       * Type of bundle that was created. (ex: "object_scan")
       */
      data_type: z.nativeEnum(BundleDataType),
      /**
       * (readonly)
       * Bundle created timestamp on document creation,
       * used for purging stale bundles that don't become
       * "bundle_ready" within 24 hours.
       */
      server_created: this.utils.date,
      /**
       * (readonly)
       * timestamp of when bundle was originally created by the app
       * (cannot be newer than now, or older than 1970-01-01 00:00:00 UTC)
       */
      created: this.utils.date,
      /**
       * (editable by owner)
       * Poster thumbnail of bundle
       *
       * Format:
       * - JPG encoding
       * - limited to 64KB
       * - resolution under 1024x768
       *   (@2x retina 512x384)
       * - aspect ratio 4:3
       * - Generally size is 640x480
       */
      poster_jpg_data: this.utils.base64String,
      /**
       * (editable by owner)
       * Poster thumbnail mask of bundle
       *
       * Format:
       * - PNG encoding
       * - Limited to 64KB
       * - Matching resolution to poster_jpg_data
       */
      poster_mask_png_data: this.utils.base64String.optional(),
      /**
       * Object scanner specific metadata that can be read if permissions
       * allow access to viewing the scan data.
       * (ie public == true, otherwise owner only)
       */
      object_scan: this.BundleSharedObjectScanDBModel.optional(),
      /**
       * Monocle Video metadata.
       */
      monocle_video: BundleMonocleVideoDBModel.optional(),
    })

    this.FirestoreBundleSharedMetadata = this.BundleSharedMetadataDBModel.merge(
      z.object({
        server_created: this.utils.dateForFirestore,
        created: this.utils.dateForFirestore,
        poster_jpg_data: this.utils.bytesForFirestore,
        poster_mask_png_data: this.utils.bytesForFirestore.optional(),
      }),
    )

    this.fromOwnerEditable = (
      oe: z.infer<typeof BundleOwnerEditableDBModel>,
    ) => {
      const pubDetails = {} as z.infer<typeof BundleSharedDetailsDBModel>
      if (oe.details_public?.title && oe.details.title)
        pubDetails.title = oe.details.title
      if (oe.details_public?.email && oe.details.email)
        pubDetails.email = oe.details.email
      if (oe.details_public?.description && oe.details.description)
        pubDetails.description = oe.details.description
      if (oe.details_public?.location_name && oe.details.location_name)
        pubDetails.location_name = oe.details.location_name
      if (oe.wall_mounted ?? false) pubDetails.wall_mounted = oe.wall_mounted

      return BundleSharedDetailsDBModel.parse(pubDetails)
    }

    this.BundleSharedDBModel = z.object({
      id: z.string(),
      version: z.string(),
      metadata: this.BundleSharedMetadataDBModel,
      details: BundleSharedDetailsDBModel,
    })

    this.FirestoreBundleSharedDBModel = this.BundleSharedDBModel.merge(
      z.object({
        metadata: this.FirestoreBundleSharedMetadata,
      }),
    )

    type BundleSharedDBModel = z.infer<typeof this.BundleSharedDBModel>

    this.sharedObjectScanFromBundleObjectScan = (
      objectScan: z.infer<typeof BundleObjectScanDBModel>,
    ): z.infer<typeof this.BundleSharedObjectScanDBModel> => {
      const sharedObjectScan = {} as z.infer<
        typeof this.BundleSharedObjectScanDBModel
      >
      sharedObjectScan.min_coordinate = objectScan.min_coordinate
      sharedObjectScan.max_coordinate = objectScan.max_coordinate
      sharedObjectScan.camera_pan = objectScan.camera_pan
      sharedObjectScan.camera_euler_angles = objectScan.camera_euler_angles
      sharedObjectScan.glb_filepath =
        objectScan.glb_edit_filepath ?? objectScan.glb_filepath
      sharedObjectScan.usdz_filepath =
        objectScan.usdz_edit_filepath ?? objectScan.usdz_filepath
      return sharedObjectScan
    }

    this.sharedMetadataFromBundleMetadata = (
      metadata: z.infer<typeof BundleMetadataDBModel>,
    ): z.infer<typeof this.BundleSharedMetadataDBModel> => {
      const sharedMetadata = {} as z.infer<
        typeof this.BundleSharedMetadataDBModel
      >
      sharedMetadata.owner = metadata.owner
      sharedMetadata.data_type = metadata.data_type
      sharedMetadata.server_created = metadata.server_created
      sharedMetadata.created = metadata.created
      sharedMetadata.poster_jpg_data = metadata.poster_jpg_data
      sharedMetadata.poster_mask_png_data = metadata.poster_mask_png_data

      if (metadata.object_scan) {
        sharedMetadata.object_scan = this.sharedObjectScanFromBundleObjectScan(
          metadata.object_scan,
        )
      }

      if (metadata.monocle_video) {
        sharedMetadata.monocle_video = metadata.monocle_video
      }
      return sharedMetadata
    }

    this.sharedBundleFromBundle = (
      bundle: z.infer<typeof BundleDBModel>,
    ): z.infer<typeof this.BundleSharedDBModel> => {
      const sharedBundle = {} as z.infer<typeof this.BundleSharedDBModel>
      sharedBundle.id = bundle.id
      sharedBundle.version = bundle.version
      sharedBundle.metadata = this.sharedMetadataFromBundleMetadata(
        bundle.metadata,
      )
      sharedBundle.details = this.fromOwnerEditable(bundle.owner_editable)
      return sharedBundle
    }

    this.sharedBundleConverter = {
      toFirestore: (bundle: BundleSharedDBModel) => {
        return this.FirestoreBundleSharedDBModel.parse(bundle)
      },

      fromFirestore: (
        snapshot: QueryDocumentSnapshot,
        options: SnapshotOptions,
      ): BundleSharedDBModel => {
        const data = snapshot.data(options)
        return this.BundleSharedDBModel.parse({ id: snapshot.id, ...data })
      },
    }
    this.adminSharedBundleConverter = {
      toFirestore: (bundle: BundleSharedDBModel) => {
        return this.FirestoreBundleSharedDBModel.parse(bundle)
      },

      fromFirestore: (
        snapshot: AdminQueryDocumentSnapshot,
      ): BundleSharedDBModel => {
        const data = snapshot.data()!
        return this.BundleSharedDBModel.parse({ id: snapshot.id, ...data })
      },
    }
  }
}
